import { useBoolean, useId } from "@uifabric/react-hooks";
import { Callout } from "office-ui-fabric-react";
import * as React from "react";
import i18n from "../../../../localization/i18n";
import { authorize, Roles } from "../../../../config/routing/actions/authService";

export interface LanguageIconProps { }

const flagStyle = { margin: "5px", boxShadow: "0px 0px 2px 1px rgba(66, 68, 90, 1)" };

const flags = {
  pl: "/flags/pl.svg",
  gb: "/flags/gb.svg",
  de: "/flags/de.svg",
  sk: "/flags/sk.svg",
  et: "/flags/et.svg",
  cs: "/flags/cs.svg",
  lt: "/flags/lt.svg",
};

const clientLanguages: { lang: string; flag: string }[] = [
  { lang: "pl", flag: flags.pl },
  { lang: "en", flag: flags.gb },
  { lang: "de", flag: flags.de },
  { lang: "sk", flag: flags.sk },
  { lang: "et", flag: flags.et },
  { lang: "cs", flag: flags.cs },
];

const adminLanguages: { lang: string; flag: string }[] = [
  { lang: "pl", flag: flags.pl },
  { lang: "en", flag: flags.gb },
  { lang: "lt", flag: flags.lt },
];

const getFlag = () => {
  const lang: string = i18n.language;
  if (lang.startsWith("pl")) return flags.pl;
  if (lang.startsWith("en")) return flags.gb;
  if (lang.startsWith("lt")) return flags.lt;
  if (lang.startsWith("et")) return flags.et;
  if (lang.startsWith("sk")) return flags.sk;
  if (lang.startsWith("de")) return flags.de;
  if (lang.startsWith("cs")) return flags.cs;
  return flags.gb;
};

export const LanguageSwitcher: React.FC<LanguageIconProps> = () => {
  const [isCalloutVisible, { toggle: toggleIsCalloutVisible }] = useBoolean(false);
  const buttonId = useId('callout-button');
  const { role } = authorize();
  const isAdmin = role === Roles.ADMIN;
  const languages = isAdmin ? adminLanguages : clientLanguages;

  // Admin panel: PL/EN/LT only, client part: PL/EN/DE/SK/ET/CS only. A language detected from the browser
  // or chosen in the other part falls back to Polish; reload so data translated by the API follows.
  // Checked only when the role is known - otherwise an admin would be treated as a client for a moment.
  React.useEffect(() => {
    if (!role) {
      return;
    }
    const baseLang = (i18n.language || "").split(/[-_]/)[0].toLowerCase();
    if (!languages.some(({ lang }) => lang === baseLang)) {
      i18n.changeLanguage("pl").then(() => {
        // reload only when the choice was saved - with blocked localStorage the detected language
        // would come back after the reload and the page would keep reloading
        let saved = false;
        try {
          saved = (localStorage.getItem("i18nextLng") || "").startsWith("pl");
        } catch (e) {
          saved = false;
        }
        if (saved) {
          window.location.reload();
        }
      });
    }
  }, [role]);

  return (
    <>
      <img
        id={buttonId}
        src={getFlag()}
        height='24px'
        width='24px'
        onClick={toggleIsCalloutVisible}
        alt=""
      />
      {isCalloutVisible && (
        <Callout
          role="dialog"
          gapSpace={0}
          target={`#${buttonId}`}
          onDismiss={toggleIsCalloutVisible}
          setInitialFocus
          style={{ marginLeft: "5px", marginRight: "5px", marginTop: "5px" }}
        >
          {languages.map(({ lang, flag }) => (
            <img
              key={lang}
              style={flagStyle}
              src={flag}
              height='24px'
              width='32px'
              alt={lang}
              onClick={() => {
                toggleIsCalloutVisible();
                if (i18n.language === lang) {
                  return;
                }
                // Data translated by the API (reports, dictionaries, client list) is fetched once, so reload the page;
                // the chosen language is kept in localStorage by the language detector
                i18n.changeLanguage(lang).then(() => window.location.reload());
              }}
            />
          ))}
        </Callout>
      )}
    </>
  );
};